import { PORT_KIND } from './ports.js'

/**
 * GPIO entity node: a GPIO entity of the selected plate (light, switch,
 * binary_sensor, …) as an event source on the page. Its only property is the
 * entity; its `events` output feeds event nodes, which trigger actions, and
 * its `state` output feeds GPIO bindings and comparisons.
 *
 * Firmware publishes GPIO input events over MQTT (state/input<pin>) but cannot
 * run commands for them yet; the Designer exports them as the page's
 * `gpio_actions` (Designer-first contract, see
 * docs/GPIO-INPUT-ACTIONS-FIRMWARE-SPEC.md).
 */
export const gpioNode = {
    kind: 'gpio',
    label: 'GPIO entity',
    icon: '⏚',
    color: '#15803d',
    ports: {
        inputs: [],
        outputs: [
            { id: 'events', label: 'Events', provides: PORT_KIND.OBJECT, hint: 'When something happens: wire to an event node' },
            {
                id: 'state', label: 'State', provides: PORT_KIND.GPIO_STATE,
                hint: 'Its current value: wire to a GPIO binding, or to Compare of a condition or switch',
            },
        ],
    },
    properties: {
        entity: { type: 'string', label: 'Entity' },
    },
    defaults: { entity: '' },
}

/**
 * GPIO binding: keeps a property of the UI objects wired to `value` in sync
 * with a property of the GPIO entity wired to `source`. Its data has the
 * shape of a `gpio_bindings.state` entry without the entity (GpioBindingModel),
 * and it exports as one on each target object.
 */
export const gpioBindingNode = {
    kind: 'binding',
    label: 'GPIO binding',
    icon: '≡',
    color: '#15803d',
    ports: {
        inputs: [{ id: 'source', label: 'GPIO entity', accepts: [PORT_KIND.GPIO_STATE], max: 1 }],
        outputs: [{ id: 'value', label: 'Value', provides: PORT_KIND.VALUE }],
    },
    properties: {
        property: { type: 'string', label: 'GPIO property' },
        target_property: { type: 'string', label: 'Target property' },
    },
    defaults: { property: '', target_property: '' },
}

// A state event carries the entity's resulting state as `val` (0/1); a sensor
// event carries its scaled value.
const STATE_PAYLOAD = { payload: ['val'], payloadMetadata: { val: { type: 'int', label: 'State (0/1)' } } }
const VALUE_PAYLOAD = { payload: ['val'], payloadMetadata: { val: { type: 'int', label: 'Value' } } }
const STATE_DOMAINS = ['light', 'switch', 'binary_sensor']

// Events of a GPIO entity (hasp_gpio.cpp gpio_event_handler): entities with a
// state report on/off, button-type inputs down/up/long/release, and sensors
// (ADC) changed (Designer-first, docs/GPIO-LOGIC-FIRMWARE-SPEC.md). `domains`
// limits an event to entities of those domains.
export const GPIO_ENTITY_EVENTS = Object.freeze({
    on:      { label: 'On',      domains: STATE_DOMAINS, ...STATE_PAYLOAD },
    off:     { label: 'Off',     domains: STATE_DOMAINS, ...STATE_PAYLOAD },
    down:    { label: 'Down',    domains: ['binary_sensor'], ...STATE_PAYLOAD },
    up:      { label: 'Up',      domains: ['binary_sensor'], ...STATE_PAYLOAD },
    long:    { label: 'Long',    domains: ['binary_sensor'], ...STATE_PAYLOAD },
    release: { label: 'Release', domains: ['binary_sensor'], ...STATE_PAYLOAD },
    changed: { label: 'Changed', domains: ['sensor'], ...VALUE_PAYLOAD },
})

/** Events of a GPIO entity; without metadata (offline/unknown entity) all of them. */
export function getGpioEventChoices(entity = null) {
    return Object.entries(GPIO_ENTITY_EVENTS)
        .filter(([, definition]) => !entity?.domain || !definition.domains || definition.domains.includes(entity.domain))
        .map(([event]) => event)
}
